import React, { useState } from "react";
import { Bell, BellRing, BellOff } from "lucide-react";
import { usePush } from "@/hooks/usePush";
import { toast } from "sonner";
import NotificationPrePromptModal from "@/components/NotificationPrePromptModal";

export default function PushToggle({ inline = false }) {
  const { status, supported, subscribe, unsubscribe, sendTest } = usePush();
  const [loading, setLoading] = useState(false);
  const [showPrePrompt, setShowPrePrompt] = useState(false);

  if (!supported) return null;

  const isOn = status === "granted-subscribed";

  const doSubscribe = async () => {
    setLoading(true);
    try {
      const r = await subscribe();
      if (r.ok) {
        toast.success("Notifiche attive – ti avviseremo per match, missioni ed emergenze");
        await sendTest();
      } else if (r.reason === "denied") {
        toast.error("Permesso negato dal browser");
      } else {
        toast.error("Impossibile attivare le notifiche");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (isOn) {
      setLoading(true);
      try {
        await unsubscribe();
        toast.success("Notifiche disattivate");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (status === "denied") {
      toast.error("Hai negato il permesso. Modifica le impostazioni del browser.");
      return;
    }
    // Primo opt-in: mostra la mini-demo PRIMA del prompt nativo.
    const needsPrompt =
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "default";
    if (needsPrompt) {
      setShowPrePrompt(true);
    } else {
      await doSubscribe();
    }
  };

  const handleAccept = async () => {
    setShowPrePrompt(false);
    await doSubscribe();
  };
  const handleLater = () => setShowPrePrompt(false);

  if (inline) {
    return (
      <>
        <button
          data-testid="push-toggle-inline"
          onClick={handleClick}
          disabled={loading || status === "denied"}
          className="px-3 py-1.5 rounded-full text-xs font-medium border transition-all disabled:opacity-50"
          style={{
            background: isOn ? "rgba(45,186,96,0.12)" : "#FFFFFF",
            color: isOn ? "#1E7E3F" : "#5C5C5C",
            borderColor: isOn ? "rgba(45,186,96,0.45)" : "#F0EBE3",
          }}
        >
          {isOn ? <BellRing className="h-3 w-3 inline mr-1" /> : <Bell className="h-3 w-3 inline mr-1" />}
          {isOn ? "Notifiche attive" : "Attiva notifiche"}
        </button>
        <NotificationPrePromptModal open={showPrePrompt} onAccept={handleAccept} onLater={handleLater} />
      </>
    );
  }

  return (
    <>
    <div
      data-testid="push-toggle-card"
      className="rounded-2xl p-4 border flex items-center gap-3 bg-white"
      style={{
        borderColor: isOn ? "rgba(45,186,96,0.45)" : "#F0EBE3",
        boxShadow: "0 4px 16px -6px rgba(0,0,0,0.06)",
      }}
    >
      <div
        className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
        style={{
          background: isOn ? "rgba(45,186,96,0.15)" : "#FAF7F0",
          color: isOn ? "#1E7E3F" : "#9A9A9A",
        }}
      >
        {isOn ? <BellRing className="h-5 w-5" /> : status === "denied" ? <BellOff className="h-5 w-5" /> : <Bell className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-[#1A1A1A]">
          {isOn ? "Notifiche push attive" : status === "denied" ? "Notifiche bloccate" : "Attiva le notifiche"}
        </div>
        <div className="text-xs text-[#7A7A7A] mt-0.5">
          {isOn
            ? "Riceverai avvisi per match, missioni ed emergenze."
            : status === "denied"
              ? "Per riattivare, sblocca dalle impostazioni del browser."
              : "Per emergenze, missioni e match nella tua zona."}
        </div>
      </div>
      <button
        data-testid="push-toggle-btn"
        onClick={handleClick}
        disabled={loading || status === "denied"}
        className="px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-40 transition-all active:scale-95"
        style={{
          background: isOn ? "#FAF7F0" : "linear-gradient(135deg, #FF7A1A 0%, #FF5C2A 100%)",
          color: isOn ? "#1A1A1A" : "#FFFFFF",
          border: isOn ? "1px solid #F0EBE3" : "none",
          boxShadow: isOn ? "none" : "0 6px 16px -4px rgba(255,122,26,0.45)",
        }}
      >
        {loading ? "…" : isOn ? "Disattiva" : "Attiva"}
      </button>
    </div>
    <NotificationPrePromptModal open={showPrePrompt} onAccept={handleAccept} onLater={handleLater} />
    </>
  );
}
